import { Fragment, Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/navigation/ScrollToTop';
import SocialFloatingActions from './components/navigation/SocialFloatingActions';
import LanguageRouter from './components/navigation/LanguageRouter';
import HomePage from './pages/HomePage';
// Rendered only after the page settles (see usePageSettled below), so their
// code lives in separate chunks instead of the main bundle.
const Analytics = lazy(() =>
  import('@vercel/analytics/react').then((m) => ({ default: m.Analytics })),
);
const SpeedInsights = lazy(() => import('./components/analytics/SpeedInsights'));
const GoogleAnalytics = lazy(() => import('./components/analytics/GoogleAnalytics'));
const UmamiAnalytics = lazy(() => import('./components/analytics/UmamiAnalytics'));
const PWAInstallPrompt = lazy(() => import('./components/navigation/PWAInstallPrompt'));
// Route-level code splitting: GuidePage pulls in the long-form guide content for
// all 8 languages (~1.5 MB of JSON). Loading it lazily keeps that weight off the
// homepage bundle entirely — crawlers still get the full article from the
// prerendered HTML, so nothing is lost for SEO.
const GuidePage = lazy(() => import('./pages/GuidePage'));
const GuidesIndexPage = lazy(() => import('./pages/GuidesIndexPage'));
const BioPage = lazy(() => import('./pages/BioPage'));
const PanelPage = lazy(() => import('./pages/PanelPage'));
import { PREFIXED_LANGUAGES, splitLanguageFromPath } from './lib/localeRoutes';
import { useVisitorTracking } from './hooks';
import { useAnalyticsReady } from './lib/deferred';

/** Scroll-section routes: every one of these renders the single-page HomePage. */
const SECTION_PATHS = [
  'bhalil',
  'rooms',
  'services',
  'gallery',
  'nearby',
  'reviews',
  'findus',
  'contact',
];

/**
 * The public route tree, rendered once at the root (English) and once under each
 * language prefix, so every language has crawlable URLs of its own.
 */
function publicRoutes(prefix: string) {
  const at = (path: string) => `${prefix}/${path}`;

  return (
    <Fragment key={prefix || 'root'}>
      <Route path={prefix || '/'} element={<HomePage />} />
      {SECTION_PATHS.map((path) => (
        <Route key={path} path={at(path)} element={<HomePage />} />
      ))}
      <Route path={at('guides')} element={<GuidesIndexPage />} />
      <Route path={at('guides/:slug')} element={<GuidePage />} />
    </Fragment>
  );
}

// Renders the application shell and route mapping for all public sections.
function App() {
  const location = useLocation();
  const { path: barePath } = splitLanguageFromPath(location.pathname);
  const isBioRoute = barePath === '/bio';
  const isPanelRoute = barePath === '/panel';

  // Analytics and visitor logging aren't needed for first render — they wait
  // for the visitor's first interaction (or 12 s), so they don't block the
  // main thread while the page loads. See src/lib/deferred.ts.
  const analyticsReady = useAnalyticsReady();

  // Track visitor page views
  useVisitorTracking(!isPanelRoute && analyticsReady);

  // Fade out the static boot skeleton (index.html) now that React has
  // painted real content — it only exists to cover the gap between first
  // paint and this point, never to replace the prerendered SEO content.
  useEffect(() => {
    const skeleton = document.getElementById('boot-skeleton');
    if (!skeleton) return;
    skeleton.classList.add('boot-skeleton--hidden');
    const timer = setTimeout(() => skeleton.remove(), 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#1a0f0a] transition-colors duration-500 overflow-x-hidden">
      <LanguageRouter />
      {!isBioRoute && !isPanelRoute && <Header />}
      <main className={isBioRoute || isPanelRoute ? '' : 'overflow-x-hidden'}>
        <Suspense fallback={null}>
        <Routes>
          {publicRoutes('')}
          {PREFIXED_LANGUAGES.map((lang) => publicRoutes(`/${lang}`))}
          {/* Single-language routes: no prefixed variants. */}
          <Route path="/panel" element={<PanelPage />} />
          <Route
            path="/bio"
            element={
              <Fragment>
                <HomePage />
                <BioPage />
              </Fragment>
            }
          />
          <Route path="*" element={<HomePage />} />
        </Routes>
        </Suspense>
      </main>
      {!isBioRoute && !isPanelRoute && <Footer />}
      {!isPanelRoute && <ScrollToTop />}
      {!isBioRoute && !isPanelRoute && <SocialFloatingActions />}
      {analyticsReady && !isPanelRoute && (
        <Suspense fallback={null}>
          <GoogleAnalytics />
          <UmamiAnalytics />
          <SpeedInsights />
          <Analytics />
          <PWAInstallPrompt />
        </Suspense>
      )}
    </div>
  );
}

export default App;
